import {useEffect, useState} from 'react'
import {useIntersectionObserver} from '@github-ui/use-sticky-header/useIntersectionObserver'

const HEADER_WRAPPER_SELECTOR = '.js-header-wrapper'
const OVERLAY_FIXED_SELECTOR = '.js-header-overlay-fixed'

// Matches the legacy scroll watcher: treat the header as "at the top" until the
// page has scrolled ~100px, then flip to the solid background.
const OBSERVER_OPTIONS: IntersectionObserverInit = {rootMargin: '100px 0px 0px 0px'}

/**
 * Drives the fixed header's `data-is-top` scroll treatment.
 *
 * The new `fixed` prop enables tracking directly. During rollout, the legacy
 * `.js-header-overlay-fixed` marker remains a fallback. The sentinel sits before the
 * Rails-owned fixed wrapper when present, or before the header in standalone renders.
 * The wrapper also receives `data-is-top` for the retained legacy treatment CSS.
 */
export function useHeaderIsTop(headerRef: React.RefObject<HTMLElement | null>, fixed: boolean): {isTop: boolean} {
  const [isTop, setIsTop] = useState(true)
  const [trackedFixed, setTrackedFixed] = useState(fixed)

  // Turning `fixed` off stops the scroll watcher, so return `isTop` to its top/transparent default
  // during render (rather than in an effect) instead of leaving a stale scrolled value that would
  // flash the opaque scrim the next time `fixed` is enabled at the top of the page.
  if (trackedFixed !== fixed) {
    setTrackedFixed(fixed)
    if (!fixed) setIsTop(true)
  }

  const sentinelRef = useIntersectionObserver(entries => {
    const entry = entries[0]
    if (!entry) return
    const atTop = entry.isIntersecting
    setIsTop(atTop)
    headerRef.current?.closest<HTMLElement>(HEADER_WRAPPER_SELECTOR)?.setAttribute('data-is-top', String(atTop))
  }, OBSERVER_OPTIONS)

  useEffect(() => {
    const header = headerRef.current
    if (!header || (!fixed && !document.querySelector(OVERLAY_FIXED_SELECTOR))) return

    const wrapper = header.closest<HTMLElement>(HEADER_WRAPPER_SELECTOR)
    const anchor = wrapper ?? header

    const sentinel = document.createElement('div')
    sentinel.setAttribute('data-scroll-watcher', 'true')
    anchor.before(sentinel)
    sentinelRef(sentinel)

    return () => {
      sentinelRef(null)
      sentinel.remove()
      // Tracking is ending (fixed turned off or unmount); clear the scrolled value we wrote so the
      // retained legacy wrapper CSS returns to its transparent top state instead of staying solid.
      wrapper?.setAttribute('data-is-top', 'true')
    }
  }, [fixed, headerRef, sentinelRef])

  return {isTop}
}
